/* eslint-disable max-len */
/* eslint-disable no-console */
import { API } from 'aws-amplify';

/**
 * TypeScript interfaces for video and podcast management
 */
export interface VideoThumbnail {
	url: string;
	canEdit?: boolean;
	canDelete?: boolean;
}

export interface VideoPlaybackProgress {
	totalPlays: number;
	completions: number;
	averageViewTime: number;
	dropOffPoints?: Array<{
		timePoint: number;
		dropOffCount: number;
	}>;
}

export interface UserInfo {
	userId: string;
	name: string;
	email?: string;
	role?: string;
}

export interface AccessControl {
	internalEmployees?: {
		allSelected?: boolean;
		departments?: Array<{ name: string; count?: number }>;
		specificUsers?: Array<UserInfo>;
	};
	userGroups?: Array<{ type: string; count?: number }>;
	participantCount?: number;
	participantLimit?: number;
}

export interface EngagementMetrics {
	views: number;
	downloads: number;
	helpful: number;
	notHelpful: number;
	averageTimeSpent: number;
	listenCount: number;
	shareCount: number;
}

export interface VideoItem {
	title: string;
	description?: string;
	_id?: string;
	url: string;
	fileType: string;
	fileSize: number;
	duration?: number;
	order?: number;
	uploadedAt?: string;
	thumbnail: VideoThumbnail;
	playbackProgress?: VideoPlaybackProgress;
	status?: string;
	platform?: string;
	videoId?: string;
	uploadedBy?: UserInfo;
	updatedBy?: UserInfo;
	playlists?: Array<{
		playlistId: string;
		orderInPlaylist: number;
	}>;
}

export interface CreateVideoPayload {
	sourceType: 'system' | 'upload' | 'embed_link' | 'media_drive';
	videos: VideoItem[];
	visibility?: 'public' | 'private' | '';
	categoryIds?: string[] | string;
	tags?: string[];
	allowDownload?: boolean;
	allowShare?: boolean;
	accessControl?: {
		internalEmployees?: {
			allSelected?: boolean;
			departments?: Array<{ name: string; count?: number }>;
			specificUsers?: Array<{ userId: string; name: string; email: string; role?: string }>;
		};
		userGroups?: Array<{ type: string; count?: number }>;
		participantCount?: number;
		participantLimit?: number;
	};
}

export interface VideoResponse {
	_id: string;
	companyId: string;
	companyName?: string;
	sourceType: 'system' | 'media_drive' | 'embed_link';
	videos: VideoItem[];
	totalDuration: number;
	hasAudio: boolean;
	videoQuality: 'HD' | '4K' | 'Standard';
	visibility: 'public' | 'private';
	allowDownload: boolean;
	allowShare: boolean;
	accessControl: AccessControl;
	categoryIds: string[];
	subCategory?: string;
	tags: string[];
	engagement: EngagementMetrics;
	uploadedBy: UserInfo;
	updatedBy: UserInfo;
	publishedBy?: UserInfo;
	publishedAt?: string;
	scheduledPublishDate?: string;
	createdAt: string;
	updatedAt: string;
	deleted: boolean;
	status?: string;
	type?: string;
	order?: number;
}

export interface VideoListResponse {
	videos: VideoResponse[];
	total: number;
}

export interface VideoListGetResponse {
	data: VideoResponse[];
	pagination: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

// Podcast specific interfaces
export interface CoverImage {
	url: string;
	altText?: string;
	canEdit?: boolean;
	canDelete?: boolean;
}

export interface PodcastAnalytics {
	totalListens: number;
	averageListeningTime: number;
	completionRate: number;
	topEpisodes: Array<{
		episodeId: string;
		title: string;
		listens: number;
	}>;
}

export interface PodcastEpisode {
	episodeId: string;
	seasonId: string;
	title: string;
	description?: string;
	audioUrl: string;
	duration: number;
	fileSize: number;
	fileType: 'mp3' | 'wav' | 'm4a' | 'ogg';
	episodeNumber: number;
	releaseDate: string;
	listens: number;
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	scheduledPublishDate?: string;
	allowDownload: boolean;
	allowShare: boolean;
	visibility: 'public' | 'private';
	accessControl?: AccessControl;
	createdAt: string;
	updatedAt: string;
	createdBy: UserInfo;
	updatedBy: UserInfo;
	publishedBy?: UserInfo;
	publishedAt?: string;
}

export interface PodcastSeason {
	seasonId: string;
	title: string;
	description?: string;
	coverImage?: CoverImage;
	episodeCount: number;
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	scheduledPublishDate?: string;
	createdAt: string;
	updatedAt: string;
	createdBy: UserInfo;
	updatedBy: UserInfo;
	publishedBy?: UserInfo;
	publishedAt?: string;
}

export interface CreatePodcastPayload {
	companyId: string;
	companyName?: string;
	sourceType: 'system' | 'media_drive' | 'embed_link';
	title: string;
	description?: string;
	categoryIds?: string[];
	subCategory?: string;
	tags?: string[];
	status?: 'draft' | 'published' | 'scheduled' | 'unpublished';
	visibility?: 'public' | 'private';
	coverImage?: CoverImage;
	seasons?: PodcastSeason[];
	episodes?: PodcastEpisode[];
	accessControl?: AccessControl;
	scheduledPublishDate?: string;
}

export interface PodcastResponse {
	_id: string;
	companyId: string;
	companyName?: string;
	sourceType: 'system' | 'media_drive' | 'embed_link';
	title: string;
	description?: string;
	categoryIds: string[];
	subCategory?: string;
	tags: string[];
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	visibility: 'public' | 'private';
	coverImage?: CoverImage;
	seasons: PodcastSeason[];
	episodes: PodcastEpisode[];
	analytics: PodcastAnalytics;
	engagement: EngagementMetrics;
	accessControl: AccessControl;
	createdBy: UserInfo;
	updatedBy: UserInfo;
	publishedBy?: UserInfo;
	createdAt: string;
	updatedAt: string;
	publishedAt?: string;
	scheduledPublishDate?: string;
}

// Playlist specific interfaces
export interface PlaylistItem {
	videoId: string;
	title: string;
	description?: string;
	thumbnailUrl?: string;
	duration: number;
	order: number;
	addedAt: string;
	addedBy: UserInfo;
}

export interface CreatePlaylistPayload {
	companyId: string;
	companyName?: string;
	name: string;
	description?: string;
	thumbnailUrl?: string;
	visibility?: 'public' | 'private';
	enabled?: boolean;
	allowShare?: boolean;
	allowDownload?: boolean;
	items?: PlaylistItem[];
	accessControl?: AccessControl;
}

export interface PlaylistResponse {
	_id: string;
	companyId: string;
	companyName?: string;
	name: string;
	description?: string;
	thumbnailUrl?: string;
	visibility: 'public' | 'private';
	enabled: boolean;
	allowShare: boolean;
	allowDownload: boolean;
	items: PlaylistItem[];
	views: number;
	contentCount: number;
	totalDuration: number;
	lastViewedAt?: string;
	accessControl: AccessControl;
	createdBy: UserInfo;
	updatedBy: UserInfo;
	createdAt: string;
	updatedAt: string;
}

export interface PlaylistListResponse {
	data: PlaylistResponse[];
	pagination: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}

// Video progress tracking interface
export interface VideoProgressTrackingResponse {
	success: boolean;
	data: {
		views: number;
		averageViewTime: number;
		completions: number;
	};
	message: string;
}

// API endpoint name as configured in index.tsx
const API_NAME = 'resourceManagementVPP';

/**
 * Track video file view progress
 * @param videoId - Video ID
 * @param fileIndex - Index of the video file (defaults to 0)
 * @param progress - Progress data containing duration and completion status
 * @returns Promise with tracking confirmation and updated stats
 */
export const trackVideoFileProgress = async (
	videoId: string,
	fileIndex: number = 0,
	progress: {
		duration: number;
		completed: boolean;
	}
): Promise<VideoProgressTrackingResponse> =>
{
	try
	{
		const response = await API.post(API_NAME, `/videos/${videoId}/files/${fileIndex}/view`, {
			body : {
				duration  : progress.duration,
				completed : progress.completed
			}
		});

		if (!response.success)
		{
			throw new Error(response.message || 'Failed to track video progress');
		}

		return response;
	}
	catch (error)
	{
		console.error('Error tracking video progress:', error);
		throw error;
	}
};

/**
 * Get list of videos for a company
 * @param companyId - Company ID
 * @param params - Query parameters (pagination, filters, etc.)
 * @returns Promise with list of videos
 */
export const getVideosList = async (
	companyId: string,
	queryStringParameters?: { skip?: number; limit?: number; status?: string; categoryId?: string, from: 'MyResource' | 'Configuration' }
): Promise<VideoListGetResponse> =>
{
	try
	{
		const response = await API.get(API_NAME, `/videos/company/${companyId}`, { queryStringParameters });

		return response;
	}
	catch (error)
	{
		console.error('Error fetching videos:', error);
		throw error;
	}
};

/**
 * Create a new video collection
 * @param companyId - Company ID
 * @param payload - Video data payload
 * @returns Promise with created video data
 */
export const createVideoCollection = async (
	companyId: string,
	payload: CreateVideoPayload
): Promise<VideoResponse> =>
{
	try
	{
		const response = await API.post(API_NAME, '/videos', {
			body : {
				companyId,
				...payload
			}
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error creating video collection:', error);
		throw error;
	}
};

/**
 * Update an existing video
 * @param videoId - Video ID to update
 * @param payload - Updated video data
 * @returns Promise with updated video data
 */
export const updateVideo = async (
	videoId: string,
	payload: Partial<CreateVideoPayload>
): Promise<VideoResponse> =>
{
	try
	{
		const response = await API.put(API_NAME, `/videos/${videoId}`,
			{
				body : payload
			});

		return response.data;
	}
	catch (error)
	{
		console.error('Error updating video:', error);
		throw error;
	}
};

/**
 * Update video status (collection or file)
 * @param videoId Video collection ID
 * @param status New status value
 * @param fileIndex Optional specific file index to update
 * @param scheduledPublishDate Required for 'scheduled' status
 * @returns Updated video data
 */
export const updateVideoStatus = async (
	videoId: string,
	status: 'draft' | 'published' | 'scheduled' | 'unpublished',
	fileIndex?: number,
	scheduledPublishDate?: number
): Promise<VideoResponse> =>
{
	try
	{
		const body: any = { status };

		let path = 'status';

		if (fileIndex !== undefined) path = `files/${fileIndex}/status`;

		if (status === 'scheduled' && !scheduledPublishDate)
		{
			throw new Error('scheduledPublishDate is required for scheduled status');
		}
		if (scheduledPublishDate) body.scheduledPublishDate = scheduledPublishDate;

		const response = await API.patch(API_NAME, `/videos/${videoId}/${path}`, { body });

		return response.data;
	}
	catch (error)
	{
		console.error('Error updating video status:', error);
		throw error;
	}
};

/**
 * Delete a video
 * @param videoId - Video ID to delete
 * @returns Promise with deletion confirmation
 */
export const deleteVideo = async (videoId: string): Promise<{ success: boolean }> =>
{
	try
	{
		await API.del(API_NAME, `/videos/${videoId}`, {});

		return { success: true };
	}
	catch (error)
	{
		console.error('Error deleting video:', error);
		throw error;
	}
};

/**
 * Get a single video by ID
 * @param videoId - Video ID to fetch
 * @returns Promise with video data
 */
export const getVideoById = async (videoId: string, queryStringParameters): Promise<VideoResponse> =>
{
	try
	{
		const response = await API.get(API_NAME, `/videos/${videoId}`, { queryStringParameters });

		if (!response.success)
		{
			throw new Error(response.message || 'Failed to fetch video');
		}

		return response.data;
	}
	catch (error)
	{
		console.error('Error fetching video:', error);
		throw error;
	}
};

export const trackVideoView = async (videoId: string): Promise<{ success: boolean; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/videos/${videoId}/view`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error tracking image view:', error);
		throw error;
	}
};

/**
 * Update video engagement metrics
 * @param videoId - Video ID
 * @param metric - Metric to update
 * @param value - Value to add
 * @returns Promise with updated engagement metrics
 */
export const updateVideoEngagement = async (
	videoId: string,
	metric: string,
	value: number
): Promise<{ success: boolean; data: { engagement: any }; message: string }> =>
{
	try
	{
		const response = await API.patch(API_NAME, `/videos/${videoId}/engagement`, {
			body : { metric, value }
		});

		return response;
	}
	catch (error)
	{
		console.error('Error updating video engagement:', error);
		throw error;
	}
};

/**
 * Toggle favorite status for a video
 * @param companyId - Company ID
 * @param videoId - Video ID
 * @returns Promise with favorite status and updated video
 */
export const toggleVideoFavorite = async (
	companyId: string,
	videoId: string
): Promise<{ success: boolean; data: { video: any; isFavorited: boolean; favoriteCount: number }; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/videos/company/${companyId}/${videoId}/favorites`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error toggling video favorite:', error);
		throw error;
	}
};

export const updateVideoDownload = async (
	videoId: string
): Promise<{ success: boolean; data: { engagement: any }; message: string }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/videos/${videoId}/download`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error updating video engagement:', error);
		throw error;
	}
};

/**
 * Update video watch progress
 * @param videoId - Video ID
 * @param watchData - Video watch progress data
 * @param deviceInfo - Device information
 * @returns Promise with update confirmation
 */
export const updateVideoWatchProgress = async (
	videoId: string,
	watchData: {
		videoIndex?: number;
		currentTime: number;
		duration: number;
		playbackSpeed?: number;
		quality?: string;
	},
	deviceInfo?: {
		deviceType?: string;
		browser?: string;
		os?: string;
		ipAddress?: string;
	}
): Promise<{ success: boolean; message: string }> =>
{
	try
	{
		const response = await API.put(API_NAME, `/watch-history/video/${videoId}/progress`, {
			body : {
				watchData,
				deviceInfo
			}
		});

		return response;
	}
	catch (error)
	{
		console.error('Error updating video watch progress:', error);
		throw error;
	}
};